import React, { useEffect, useState } from 'react';
import { Alert, Form, Input, Select } from 'antd';
import type { FormInstance } from 'antd';
import type { ContractModel } from '@/core/models/contracts/contract.model';
import type { CustomerCodeConfiguration } from '@/core/models/customer-code-settings.model';
import { availableCustomerCodeVillages } from '@/core/utils/customer-code';
import { customerCodeSettingsRepo } from '@/core/repos/customer-code-settings.repo';

export function CustomerCodeFields({ form, configuration, previewEnabled = true }: {
  form: FormInstance<ContractModel>;
  configuration: CustomerCodeConfiguration;
  previewEnabled?: boolean;
}) {
  const tollAreaId = Form.useWatch('tollAreaId', form);
  const villageId = Form.useWatch('villageId', form);
  const unitTypeId = Form.useWatch('unitTypeId', form);
  const communeId = Form.useWatch('communeId', form);
  const needsVillage = configuration.scope === 'Village' || configuration.scope === 'VillageAndType' || configuration.formula.includes('{THON:');
  const needsCommune = configuration.scope === 'Commune' || configuration.formula.includes('{XA:');
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const villages = availableCustomerCodeVillages(configuration, tollAreaId);
  const selected = villages.length === 1 ? villages[0] : undefined;
  // A submitted request retains its original geography so a retry keeps the same fingerprint.
  const pendingGeography = !previewEnabled && !!villageId;
  const mappingError = needsVillage && tollAreaId && !pendingGeography
    ? villages.length === 0
      ? 'Khu vực đã chọn chưa được cấu hình mã khách hàng. Vui lòng liên hệ quản trị để cập nhật cấu hình.'
      : villages.length > 1
        ? 'Khu vực đã chọn có nhiều dòng cấu hình cấp mã. Vui lòng liên hệ quản trị để kiểm tra cấu hình.'
        : ''
    : '';

  useEffect(() => {
    if (!needsVillage || !previewEnabled && form.getFieldValue('villageId')) return;
    const matches = availableCustomerCodeVillages(configuration, form.getFieldValue('tollAreaId'));
    const area = matches.length === 1 ? matches[0] : undefined;
    form.setFieldsValue({ villageId: area?.id, communeId: area?.communeId });
  }, [tollAreaId, configuration, form, needsVillage, previewEnabled]);

  useEffect(() => {
    let current = true;
    setPreview('');
    setError('');
    if (!previewEnabled || mappingError || !unitTypeId
      || needsVillage && (!tollAreaId || !selected || villageId !== selected.id || communeId !== selected.communeId)
      || needsCommune && !communeId) return;
    const timer = setTimeout(() => {
      void customerCodeSettingsRepo.preview({ input: { communeId, villageId, unitTypeId, tollAreaId } })
        .then(result => { if (current) setPreview(result.code); })
        .catch(reason => { if (current) setError(reason.message || 'Không xem thử được mã.'); });
    }, 250);
    return () => { current = false; clearTimeout(timer); };
  }, [communeId, villageId, unitTypeId, tollAreaId, configuration.version, needsVillage, needsCommune, previewEnabled, mappingError, selected?.id, selected?.communeId]);

  return <>
    {needsVillage && <Form.Item name="villageId" hidden rules={[{ validator: (_, value) =>
      mappingError || !value ? Promise.reject(new Error(mappingError || 'Chọn khu vực để cấp mã khách hàng.')) : Promise.resolve()
    }]}><Input /></Form.Item>}
    {needsCommune && !needsVillage
      ? <Form.Item name="communeId" label="Xã cấp mã khách hàng" rules={[{ required: true, message: 'Chọn xã cấp mã.' }]}>
        <Select showSearch optionFilterProp="label" placeholder="Chọn xã" options={configuration.communes.map(commune => ({ value: commune.id, label: commune.name }))} />
      </Form.Item>
      : <Form.Item name="communeId" hidden><Input /></Form.Item>}
    <Form.Item name="clientRequestId" hidden><Input /></Form.Item>
    <div className="col-span-2 mb-4"><Alert type={mappingError || error ? 'warning' : 'info'} showIcon
      message={!previewEnabled ? 'Yêu cầu tạo tự động trước đó được giữ để thử lại; không cần nhập mã thủ công.'
        : mappingError || error || (preview ? `Mã dự kiến: ${preview}. Không giữ số; mã được cấp khi lưu.`
          : 'Mã khách hàng được cấp tự động khi lưu. Chọn khu vực và loại đơn vị để xem thử.')} /></div>
  </>;
}
